// client/src/components/ticketing/RegistrationFieldBuilder.jsx
//
// CRUD panel for custom registration form fields on a ticketed event item
// (server/routes/ticketingRoutes.js — t_registration_field_def). Rendered as
// a tab inside TicketConfig.jsx, sharing its AuctionConfig.css theme.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const FIELD_TYPES = ["text", "textarea", "select", "checkbox", "date", "phone", "email"];

const EMPTY_FIELD = {
  field_key: "",
  label: "",
  field_type: "text",
  required: false,
  options: "",
};

export default function RegistrationFieldBuilder({ tenantId, eventId, itemId }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}/item/${itemId}`;

  const [fields, setFields] = useState([]);
  const [form, setForm] = useState(EMPTY_FIELD);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const loadFields = useCallback(async () => {
    try {
      const { data } = await axios.get(`${base}/fields`);
      if (data.success) setFields(data.fields);
    } catch (err) {
      console.error("Error loading registration fields:", err);
    }
  }, [base]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const submitField = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: "", type: "" });

    const payload = {
      field_key: form.field_key.trim(),
      label: form.label.trim(),
      field_type: form.field_type,
      required: !!form.required,
      options:
        form.field_type === "select" && form.options.trim()
          ? form.options.split(",").map((s) => s.trim()).filter(Boolean)
          : undefined,
    };

    try {
      const { data } = await axios.post(`${base}/fields`, payload);
      if (data.success) {
        setMessage({ text: data.message, type: "success" });
        setForm(EMPTY_FIELD);
        loadFields();
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to create field", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const deleteField = async (fieldId) => {
    if (!window.confirm("Delete this registration field?")) return;
    try {
      await axios.delete(`${base}/fields/${fieldId}`);
      loadFields();
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to delete field", type: "error" });
    }
  };

  return (
    <div>
      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      <form onSubmit={submitField} className="auction-config-form" style={{ padding: 16, borderRadius: 12, marginBottom: 16 }}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="field_key">Field Key <span className="required">*</span></label>
            <input
              id="field_key"
              value={form.field_key}
              onChange={(e) => setForm((f) => ({ ...f, field_key: e.target.value }))}
              placeholder="dietary_restrictions"
              pattern="[a-zA-Z0-9_]+"
              title="Letters, numbers, and underscores only"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="field_label">Label <span className="required">*</span></label>
            <input
              id="field_label"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="Shown to attendees"
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="field_type">Field Type</label>
            <select
              id="field_type"
              value={form.field_type}
              onChange={(e) => setForm((f) => ({ ...f, field_type: e.target.value }))}
            >
              {FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          {form.field_type === "select" && (
            <div className="form-group">
              <label htmlFor="field_options">Choices</label>
              <input
                id="field_options"
                value={form.options}
                onChange={(e) => setForm((f) => ({ ...f, options: e.target.value }))}
                placeholder="Comma-separated choices"
              />
            </div>
          )}
        </div>
        <div className="form-group">
          <label className="ticket-checkbox-row">
            <input
              type="checkbox"
              checked={form.required}
              onChange={(e) => setForm((f) => ({ ...f, required: e.target.checked }))}
              style={{ width: 18, height: 18, accentColor: "#ff6800" }}
            />
            Required field
          </label>
        </div>
        <div className="form-actions" style={{ marginTop: 12, paddingTop: 12 }}>
          <button type="submit" disabled={saving} className="save-button">
            {saving ? "Saving…" : "Add Field"}
          </button>
        </div>
      </form>

      {fields.length === 0 ? (
        <div className="ticket-empty-state">No custom registration fields yet.</div>
      ) : (
        <ul className="ticket-list">
          {fields.map((field) => (
            <li key={field.id} className="ticket-list-item">
              <div>
                <div className="ticket-list-item-title">
                  {field.label} {field.required ? <span style={{ color: "#ff6800" }}>*</span> : null}
                </div>
                <div className="ticket-list-item-meta">
                  {field.field_key} · {field.field_type}
                  {field.eventitemid == null ? " · event-wide" : ""}
                </div>
              </div>
              <button onClick={() => deleteField(field.id)} className="ticket-item-btn danger">
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
